import { useEffect, useState } from "react";
import { apiClient, formatApiError, uploadFile, resolveUrl } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { toast } from "sonner";
import { Instagram, Plus, X, Upload, Save, ExternalLink } from "lucide-react";
import { PageHeader, Card, Field, inputCls, btnPrimary, btnGhost } from "@/components/admin/ui";

const emptyPost = { image_url: "", post_url: "", caption: "" };

export default function InstagramPage() {
    const { settings, refresh } = useSettings();
    const [form, setForm] = useState(settings);

    useEffect(() => { setForm(settings); }, [settings]);

    const feed = form.instagram_feed || [];

    const addPost = () => setForm({ ...form, instagram_feed: [...feed, { ...emptyPost }] });
    const updatePost = (i, key, val) => setForm({ ...form, instagram_feed: feed.map((p, idx) => idx === i ? { ...p, [key]: val } : p) });
    const removePost = (i) => setForm({ ...form, instagram_feed: feed.filter((_, idx) => idx !== i) });

    const upload = async (e, i) => {
        const f = e.target.files?.[0];
        if (!f) return;
        try {
            const r = await uploadFile(f);
            updatePost(i, "image_url", r.url);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const save = async (e) => {
        e.preventDefault();
        try {
            await apiClient.put("/settings", form);
            await refresh();
            toast.success("Instagram feed saved.");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <>
            <PageHeader
                title="Instagram"
                subtitle="Connect your Instagram account info and curate the feed shown on the website."
            />

            <form onSubmit={save} className="space-y-6 max-w-3xl" data-testid="instagram-form">
                <Card className="p-6">
                    <h3 className="font-serif text-xl mb-4 flex items-center gap-2"><Instagram size={18} /> Account</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Instagram username (without @)">
                            <input data-testid="ig-username" className={inputCls} value={form.instagram_username || ""} onChange={(e) => setForm({ ...form, instagram_username: e.target.value })} />
                        </Field>
                        <Field label="Profile URL">
                            <input className={inputCls} value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} />
                        </Field>
                    </div>
                    <div className="text-xs text-stone-muted mt-4">
                        <strong>Note:</strong> Real Instagram Graph API OAuth requires a Facebook App + business account. For now, curate the public feed manually below.
                        {form.instagram_username && <> <a href={`https://instagram.com/${form.instagram_username}`} target="_blank" rel="noreferrer" className="text-walnut hover:underline inline-flex items-center gap-1 ml-2"><ExternalLink size={11} /> View profile</a></>}
                    </div>
                </Card>

                <Card className="p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-serif text-xl">Curated feed ({feed.length} posts)</h3>
                        <button type="button" onClick={addPost} data-testid="ig-add-post" className={btnGhost}><Plus size={14} /> Add post</button>
                    </div>
                    <div className="space-y-4">
                        {feed.map((p, i) => (
                            <div key={i} className="flex gap-3 items-start border border-stone-border dark:border-stone-800 p-3">
                                {p.image_url ? <img src={resolveUrl(p.image_url)} alt="" className="w-20 h-20 object-cover bg-sand" /> : <div className="w-20 h-20 bg-sand dark:bg-stone-800" />}
                                <div className="flex-1 space-y-2">
                                    <input className={inputCls} placeholder="Image URL" value={p.image_url} onChange={(e) => updatePost(i, "image_url", e.target.value)} />
                                    <input className={inputCls} placeholder="Instagram post URL" value={p.post_url} onChange={(e) => updatePost(i, "post_url", e.target.value)} />
                                    <input className={inputCls} placeholder="Caption (optional)" value={p.caption} onChange={(e) => updatePost(i, "caption", e.target.value)} />
                                    <label className={btnGhost + " cursor-pointer text-xs"}><Upload size={12} /> Upload image<input type="file" accept="image/*" onChange={(e) => upload(e, i)} className="hidden" /></label>
                                </div>
                                <button type="button" onClick={() => removePost(i)} className="text-stone-muted hover:text-red-700"><X size={16} /></button>
                            </div>
                        ))}
                        {!feed.length && <div className="text-sm text-stone-muted text-center py-6">No posts curated yet.</div>}
                    </div>
                </Card>

                <button data-testid="ig-save" className={btnPrimary}><Save size={14} /> Save</button>
            </form>
        </>
    );
}
